import { Box, Button, Stack } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';
import { ComposedParametersValue } from '../../../../../../redux/CUParameters/selectors';
import { ParameterProps } from './Parameter';
import { ParameterEditControl } from './ParameterEditControl/ParameterEditControl';
import { getParameterTitle } from '../../../../../../redux/CUParameters/api/util/parameterUtil';
import { SingleData } from '../../../../../../theme/components';
import { useShowErrorMessage } from '../../../../../../handlingErrors';
import { getPolyglot } from '../../../../../../i18n';
import usePreferredTemperatureUnit from '../../../../../../redux/settings/hooks/usePreferredTemperatureUnit';
import _ from 'lodash';
import {
  getDecimals,
  getPreferredUnit,
  getValueWithPreferredUnitAndRounded,
} from '../../../../../../redux/controlUnit/api/util/sensor/formatValues';
import {
  fahrenheitToCelsius,
  TemperatureUnit,
} from '../../../../../../customized/fresh-watch/theme/utils/temperatureUtils';
import {
  DEFAULT_DECIMALS,
  DEFAULT_UNIT_SENSOR,
} from '../../../../../../redux/controlUnit/api/util/constants';

export interface ParameterEditProps extends ParameterProps {
  onClickClose: () => void;
  onSave: (parameter: ComposedParametersValue, newValue: string) => void;
  loading?: boolean;
}
export function ParameterEdit(props: ParameterEditProps) {
  const { parameter, loading, onClickClose, onSave } = props;
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const temperatureUnit = getPreferredUnit(
    parameter.unitString ?? parameter.unit,
    preferredTemperatureUnit
  ) as TemperatureUnit;

  const decimals = getDecimals(parameter.decimals, preferredTemperatureUnit);
  const title = getParameterTitle(parameter, preferredTemperatureUnit);

  const incomingFixedValue = getValueWithPreferredUnitAndRounded(
    parameter.value
      ? parseFloat(parameter.value)
      : _.ceil(parameter.min, decimals),
    {
      decimals: parameter.decimals,
      unitString: parameter.unitString ?? parameter.unit ?? DEFAULT_UNIT_SENSOR,
      maxVal: parameter.max,
      minVal: parameter.min,
      id: parameter.name,
      name: parameter.name,
      unit: parameter.unit,
    },
    preferredTemperatureUnit
  );

  const [temporalValue, setTemporalValue] = useState(
    incomingFixedValue.toString()
  );
  useEffect(() => {
    setTemporalValue(incomingFixedValue.toString());
  }, [parameter.value]);

  const [errorForm, setErrorForm] = useState<string | null | undefined>(
    undefined
  );

  const resetValue = (value: number) => {
    /* Transforms back information to °C */
    if (temperatureUnit === '°F')
      value = _.round(
        fahrenheitToCelsius(value),
        parameter.decimals ?? DEFAULT_DECIMALS
      );

    return Number(value);
  };

  const showErrorMessage = useShowErrorMessage();
  const polyglot = getPolyglot();
  const onClickSave = () => {
    const result = resetValue(parseFloat(temporalValue));
    if (errorForm) return showErrorMessage(errorForm);
    onSave(parameter, result.toString());
  };
  const onChange = (value: string, error?: string) => {
    setTemporalValue(value);
    setErrorForm(error);
  };
  return (
    <SingleData
      key={parameter.name}
      header={title}
      body={
        <Box m={1}>
          <ParameterEditControl
            {...props}
            value={temporalValue}
            onChangeValue={onChange}
          />
          <Stack
            direction={'row'}
            justifyContent={'flex-end'}
            mt={2}
            spacing={1}
            fontSize={'1rem'}
          >
            <Button
              onClick={onClickClose}
              variant="text"
              disabled={loading}
              size="small"
            >
              {polyglot.t('button.cancel')}
            </Button>
            <Button
              onClick={onClickSave}
              variant="contained"
              disabled={loading}
              size="small"
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                polyglot.t('button.save')
              )}
            </Button>
          </Stack>
        </Box>
      }
      description={parameter.descriptionTranslation}
    />
  );
}
